import { useEffect, useState } from "react";
import { Box, Typography, Grid, Avatar, Divider, Tooltip, Chip, Rating } from "@mui/material";
import { useHistory, useParams, useLocation } from "react-router-dom";
import ReusableHeadingComponents from "../../components/reusableComponents/reusableHeadingComponents";
import CustomTabs from "../../components/reusableComponents/CustomTabs";
import ReusableChip from "../../components/reusableComponents/chipSelector";
import ReusableButton from "../../components/reusableComponents/reusableButtonComponents";
import ReusableMuiIcons from "../../_helpers/ReusableMuiIcons";
import ApiService from "../../_services/ApiService";
import useSnackDispatcher from "../../_helpers/reusablefunctions/snackBarComponent";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import TabsGigApplicationData from "./TabsGigApplicationData";

const tabs = ["pitch", "deliverable", "livepost"];
const tabLabels = {
    pitch: "Pitch",
    deliverable: "Deliverables Status",
    livepost: "Live Post Status",
};

// const disabledReasons = {
//     deliverable: 'Pitch is not approved yet',
//     livepost: 'Deliverable is not approved yet'
// };

export default function ViewGigApplication() {
    const history = useHistory();
    const location = useLocation();
    const { id, gigId, tab, innerTab } = useParams();
    const [activeTab, setActiveTab] = useState(tab || "pitch");

    const [showFeedbackBox, setShowFeedbackBox] = useState(false);
    const [feedbackText, setFeedbackText] = useState('');
    const [feedbackSentIndexes, setFeedbackSentIndexes] = useState([]);
    const [expandedIndexes, setExpandedIndexes] = useState([]);
    const [submittedFeedbackTexts, setSubmittedFeedbackTexts] = useState({});
    const [applicationData, setApplicationData] = useState(null);
    const [isValidMap, setIsValidMap] = useState({});
    const [reasonMap, setReasonMap] = useState({});
    // const [activeTabs, setActiveTabs] = useState(innerTab || 'Submission');
    const [activeTabs, setActiveTabs] = useState(() => {
        if (innerTab) {
            // Convert URL format back to display format
            // "updated-submission" -> "Updated Submission"
            return innerTab.split('-').map(word =>
                word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ');
        }
        return 'Submission';
    });
    const [handleRating, setHandleRating] = useState("");
    const [loading, setLoading] = useState({
        approvePitch: false,
        rejectPitch: false,
        approveDeliverable: false,
        rejectDeliverable: false,
        requestChanges: false,
        sendFeedback: false,
        save: false,
        markAsClosed: false
    });
    const [rejectFeedbackText, setRejectFeedbackText] = useState("");
    const [showRejectFeedbackBox, setShowRejectFeedbackBox] = useState(false);
    const [loadingMap, setLoadingMap] = useState({});

    const { successSnack, errorSnack } = useSnackDispatcher();

    const niches = applicationData?.creator_handle_niche && applicationData.creator_handle_niche.trim() !== ""
        ? applicationData.creator_handle_niche
            .split(",")
            .map((n) => n.trim())
            .filter((n) => n.length > 0)
        : [];

    const visibleNiches = niches.slice(0, 3);
    const hiddenNiches = niches.slice(3);

    const capitalizeFirstLetter = (value) => {
        if (!value || typeof value !== "string") return "Pending";
        return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    };

    useEffect(() => {
        if (tab) {
            setActiveTab(tab);
        }
    }, [tab]);

    useEffect(() => {
        if (innerTab) {
            const formattedTab = innerTab.split('-').map(word =>
                word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ');
            setActiveTabs(formattedTab);
        }
    }, [innerTab]);

    const handleInnerTabChange = (newTab) => {
        setActiveTabs(newTab);
        const routeTab = tabRouteMap[activeTab] ?? activeTab;
        const innerTabRoute = newTab.replace(' ', '-').toLowerCase();
        history.push(`/gig-management/view-gig/${gigId}/application/${id}/${routeTab}/${innerTabRoute}`);
    };

    const getPlatformIcon = (type) => {
        switch ((type || "").toLowerCase()) {
            case "instagram":
                return <InstagramIcon fontSize="small" sx={{ color: "#E1306C", mr: 1 }} />;
            case "youtube":
                return <YouTubeIcon fontSize="small" sx={{ color: "red", mr: 1 }} />;
            default:
                return null;
        }
    };

    useEffect(() => {
        if (id) {
            fetchApplicationData();
        }
    }, [id]);

    const fetchApplicationData = async () => {
        try {
            const res = await ApiService.get("gigs_apply", {}, id);
            if (res?.status === 200) {
                setApplicationData(res?.data?.data?.[0]);
            }
        } catch (error) {
            console.error("Error fetching application data:", error);
            errorSnack("Failed to fetch application data");
        }
    };

    useEffect(() => {
        const getHandleRating = async () => {
            try {
                const res = await ApiService.get("get_handle_rating", { handle: applicationData?.creator_handle_id });
                setHandleRating(res?.data?.average_rating);
            } catch (error) {
                console.error("Error fetching handle rating:", error);
            }
        };

        if (applicationData?.creator_handle_id) {
            getHandleRating();
        }
    }, [applicationData?.creator_handle_id]);

    const handleSave = async (idToSave, isValidOverride) => {
        setLoadingMap(prev => ({ ...prev, [idToSave]: true }));
        try {
            const isValid = isValidOverride !== undefined ? isValidOverride : isValidMap[idToSave];
            const reason = reasonMap[idToSave] || "";

            if (isValid === "No" && !reason.trim()) {
                errorSnack("Please enter a reason");
                setLoadingMap(prev => ({ ...prev, [idToSave]: false }));
                return;
            }

            // Determine which type of link we're updating based on the active tab
            let payload = {};

            if (activeTab === "deliverable") {
                payload = {
                    deliverable_status: isValid === "Yes" ? "Approved" : "Need-changes",
                    deliverable_reason: isValid === "No" ? reason : "",
                };
            } else if (activeTab === "livepost") {
                payload = {
                    link_status: isValid === "Yes" ? "Approved" : "Need-changes",
                    link_reason: isValid === "No" ? reason : "",
                };
            }

            const res = await ApiService.put("live_links", payload, null, idToSave);
            if (res?.status === 200 || res?.status === 201) {
                successSnack(isValid === "Yes"
                    ? `${activeTab === "deliverable" ? "Deliverable" : "Link"} approved successfully`
                    : `${activeTab === "deliverable" ? "Deliverable" : "Link"} marked as needs changes`
                );
                await fetchApplicationData();
            }
        } catch (error) {
            console.error("Error saving:", error);
            errorSnack(`Failed to update ${activeTab === "deliverable" ? "deliverable" : "link"} status`);
        } finally {
            setLoadingMap(prev => ({ ...prev, [idToSave]: false }));
        }
    };

    const handleApprovePitch = async () => {
        setLoading(prev => ({ ...prev, approvePitch: true }));
        try {
            const payload = { Status: "approved" };
            const res = await ApiService.put("gigs_apply_updation", payload, null, id);
            if (res?.status === 200 || res?.status === 201) {
                successSnack("Pitch approved successfully");
                await fetchApplicationData();
            }
        } catch (error) {
            console.error("Error approving pitch:", error);
            errorSnack("Failed to approve pitch");
        } finally {
            setLoading(prev => ({ ...prev, approvePitch: false }));
        }
    };

    const handleRejectPitch = async (selectedReasons, otherMessage) => {
        setLoading(prev => ({ ...prev, rejectPitch: true }));
        try {
            const payload = {
                Status: "rejected",
                rejection_reasons: selectedReasons,
                other_message: otherMessage
            };
            const res = await ApiService.put("gigs_apply_updation", payload, null, id);
            if (res?.status === 200 || res?.status === 201) {
                successSnack("Pitch rejected");
                await fetchApplicationData();
            }
        } catch (error) {
            console.error("Error rejecting pitch:", error);
            errorSnack("Failed to reject pitch");
        } finally {
            setLoading(prev => ({ ...prev, rejectPitch: false }));
        }
    };

    const handleApproveDeliverable = async (index) => {
        setLoading(prev => ({ ...prev, approveDeliverable: true }));
        try {
            const parentDeliverable = applicationData?.deliverables_links?.[0];
            let deliverable;

            if (index === 0) {
                deliverable = parentDeliverable;
            } else if (index === 1) {
                deliverable = parentDeliverable?.child;
            }

            if (!deliverable || !deliverable.id) {
                errorSnack("Deliverable not found");
                return;
            }

            const payload = {
                gig: id,
                deliverable_status: "Approved",
                deliverable_reason: ""
            };

            const res = await ApiService.put("deliverable_links", payload, null, deliverable.id);
            if (res?.status === 200 || res?.status === 201) {
                successSnack("Deliverable approved successfully");
                await fetchApplicationData();
            }
        } catch (error) {
            console.error("Error approving deliverable:", error);
            errorSnack("Failed to approve deliverable");
        } finally {
            setLoading(prev => ({ ...prev, approveDeliverable: false }));
        }
    };

    const handleRejectDeliverable = async (index) => {
        if (!rejectFeedbackText.trim()) {
            errorSnack("Please provide a reason for rejection");
            return;
        }

        setLoading(prev => ({ ...prev, rejectDeliverable: true }));
        try {
            const parentDeliverable = applicationData?.deliverables_links?.[0];
            let deliverable;

            if (index === 0) {
                deliverable = parentDeliverable;
            } else if (index === 1) {
                deliverable = parentDeliverable?.child;
            }

            if (!deliverable || !deliverable.id) {
                errorSnack("Deliverable not found");
                return;
            }

            const payload = {
                gig: id,
                deliverable_status: "Rejected",
                deliverable_reason: rejectFeedbackText
            };

            const res = await ApiService.put("deliverable_links", payload, null, deliverable.id);
            if (res?.status === 200 || res?.status === 201) {
                successSnack("Deliverable rejected");
                setRejectFeedbackText("");
                setShowRejectFeedbackBox(false);
                await fetchApplicationData();
            }
        } catch (error) {
            console.error("Error rejecting deliverable:", error);
            errorSnack("Failed to reject deliverable");
        } finally {
            setLoading(prev => ({ ...prev, rejectDeliverable: false }));
        }
    };

    const handleRequestChangesClick = () => {
        setShowFeedbackBox(true);
    };

    const handleSendFeedback = async (index) => {
        if (!feedbackText.trim()) {
            errorSnack("Please provide feedback");
            return;
        }

        setLoading(prev => ({ ...prev, sendFeedback: true }));
        try {
            const parentDeliverable = applicationData?.deliverables_links?.[0];
            let deliverable;

            if (index === 0) {
                deliverable = parentDeliverable;
            } else if (index === 1) {
                deliverable = parentDeliverable?.child;
            }

            if (!deliverable || !deliverable.id) {
                errorSnack("Deliverable not found");
                return;
            }

            const payload = {
                gig: id,
                deliverable_status: "Need-changes",
                deliverable_reason: feedbackText
            };

            const res = await ApiService.put("deliverable_links", payload, null, deliverable.id);
            if (res?.status === 200 || res?.status === 201) {
                successSnack("Feedback sent successfully");
                setSubmittedFeedbackTexts(prev => ({ ...prev, [index]: feedbackText }));
                setFeedbackSentIndexes(prev => [...prev, index]);
                setFeedbackText("");
                setShowFeedbackBox(false);
                await fetchApplicationData();
            }
        } catch (error) {
            console.error("Error sending feedback:", error);
            errorSnack("Failed to send feedback");
        } finally {
            setLoading(prev => ({ ...prev, sendFeedback: false }));
        }
    };

    const toggleExpand = (index) => {
        setExpandedIndexes(prev =>
            prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
        );
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12 || 12;
        return `${day}/${month}/${year} ${hours}:${minutes} ${ampm}`;
    };

    const tabRouteMap = {
        pitch: "pitch",
        deliverable: "deliverable",
        livepost: "livepost",
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        const routeTab = tabRouteMap[tab] ?? tab;
        history.push(`/gig-management/view-gig/${gigId}/application/${id}/${routeTab}`);
    };

    const handleMarkAsClosed = async () => {
        setLoading(prev => ({ ...prev, markAsClosed: true }));
        try {
            const payload = { closed: true };
            const res = await ApiService.put("gigs_apply_updation", payload, null, id);
            if (res?.status === 200 || res?.status === 201) {
                successSnack("Application marked as closed");
                await fetchApplicationData();
            }
        } catch (error) {
            console.error("Error marking as closed:", error);
            errorSnack("Failed to mark as closed");
        } finally {
            setLoading(prev => ({ ...prev, markAsClosed: false }));
        }
    };

    const closeMessage = () => {
        return (
            <Box p={2} mt={2} display={"flex"} flexDirection={"column"} sx={{ background: '#ffffff', borderRadius: 2 }} justifyContent={'center'} alignItems={"center"} gap={1.5}>
                <Box display={"flex"} flexDirection={"row"} gap={1} justifyContent={'center'} sx={{ background: '#F6FFFD', borderRadius: 2, p: 3, width: { md: '804px', xs: '100%' } }}>
                    <ReusableMuiIcons name={"clear"} sx={{ color: '#FF4D49', fontSize: '24px' }} />
                    <Typography sx={{ fontSize: '16px', fontWeight: 500, color: "#141414B2" }}>This offer is closed. You can explore other available offers.</Typography>
                </Box>
                <Box display={"flex"} justifyContent={'center'} textAlign={"center"} sx={{ background: '#F8F8F8', padding: '8px 16px', borderRadius: 2, width: { md: '650px', xs: '100%' } }}>
                    <Typography sx={{ color: "#141414B2", fontSize: '14px' }}>You can no longer submit or update your application for this offer.</Typography>
                </Box>
            </Box>
        )
    }

    const rejectMessage = (stepName, rejection_reasons, other_message) => {
        let reasonsText = "";

        if (Array.isArray(rejection_reasons)) {
            reasonsText = rejection_reasons
                .filter(r => r && r.trim())
                .join(", ");
        } else if (typeof rejection_reasons === "string") {
            reasonsText = rejection_reasons.trim();
        }

        const hasReason = Boolean(reasonsText);
        const hasOther = Boolean(other_message?.trim());

        if (!hasReason && !hasOther) return null;

        return (
            <Box p={2} mt={2} display="flex" flexDirection="row" sx={{ background: '#ffffff', borderRadius: 2 }}>
                <Box p={2} display="flex" flexDirection="row" gap={1} alignItems="center" sx={{ background: '#FBF5F5', width: '100%', borderRadius: 2 }}>
                    <ReusableMuiIcons name='cancel' sx={{ color: '#FF4D49', fontSize: '24px' }} />
                    <Box sx={{ display: "flex", flexDirection: 'column', gap: 0.25 }}>
                        <Typography sx={{ color: "#4C4E64", fontSize: '18px', fontWeight: 600 }}>
                            {stepName} Rejected
                        </Typography>

                        {hasReason && (
                            <Typography component="div" sx={{ color: "#141414B2", fontSize: '12px', fontWeight: 400 }}>
                                {reasonsText}
                            </Typography>
                        )}

                        {hasOther && (
                            <Typography sx={{ color: "#141414B2", fontSize: '12px', fontWeight: 400 }}>
                                {other_message}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>
        );
    };

    const formatFollowers = (num) => {
        if (num === null || num === undefined) return "";
        if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "m";
        if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
        return num.toString();
    };
    const isPitchApproved = applicationData?.Status === "approved";

    const hasDeliverables =
        Array.isArray(applicationData?.deliverables_links) &&
        applicationData.deliverables_links.length > 0;

    const areDeliverablesApproved =
        applicationData?.deliverables_status === "Approved";

    const hasLiveLinks =
        Array.isArray(applicationData?.live_links) &&
        applicationData.live_links.length > 0;

    const enabledTabs = tabs.filter((tab) => {
        if (tab === "pitch") return true;

        if (tab === "deliverable") {
            return isPitchApproved && hasDeliverables;
        }

        if (tab === "livepost") {
            return areDeliverablesApproved && hasLiveLinks;
        }

        return false;
    });

    const disabledReasons = {
        deliverable: !isPitchApproved
            ? "Pitch is not approved yet"
            : !hasDeliverables
                ? "No deliverables submitted yet"
                : "",

        livepost: !areDeliverablesApproved
            ? "Deliverables are not approved yet"
            : !hasLiveLinks
                ? "No live post submitted yet"
                : ""
    };


    const canBeClosed =
        applicationData?.Status === "approved" &&
        applicationData?.deliverables_status === "Approved" &&
        (applicationData?.live_status === "Approved" || applicationData?.live_status === "Rejected");

    const backClickHistory = () => {
        history.push(`/gig-management/view-gig/${gigId}/application`)
    }
    return (
        <Box p={4}>
            <ReusableHeadingComponents
                heading="Gig Application"
                bacHistory={true}
                clickHandlerBack={backClickHistory}
            />
            <Grid container spacing={2} >

                <Grid item xs={12} md={8}>
                    <Box sx={{ backgroundColor: "#fff", p: 3, borderRadius: 2, mb: 2 }}>
                        <Box sx={{ display: "flex", gap: 2 }}>
                            <Avatar src={applicationData?.creator_logo} sx={{ width: 81, height: 81 }} />
                            <Box>
                                <Box display="flex" gap={2}>
                                    <Typography variant="h6" sx={{ fontSize: '14px', color: '#1A1A1A', fontWeight: 600 }}>
                                        {applicationData?.handle_name}
                                    </Typography>
                                    {applicationData?.creator_type && (<ReusableChip
                                        label={applicationData?.creator_type}
                                        size="small"
                                        sx={{
                                            border: '1px solid #ED3237',
                                            color: '#ED3237',
                                            fontSize: '11px',
                                            borderRadius: '4px',
                                            backgroundColor: '#ffffff !important',
                                        }}
                                    />)}
                                </Box>
                                <Typography variant="body2" sx={{ color: "#666" }}>
                                    {applicationData?.location}
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    {(() => {
                                        let iconName;
                                        const type = applicationData?.creator_handler_type?.toLowerCase();
                                        if (type === 'instagram') {
                                            iconName = "insta";
                                        }
                                        else if (type === 'youtube') {
                                            iconName = 'youtube';
                                        } else if (type === 'facebook') {
                                            iconName = 'facebook';
                                        }
                                        return (
                                            <>
                                                <ReusableMuiIcons name={iconName} sx={{ fontSize: 18 }} />
                                                <Typography variant="body2" sx={{ color: "#72E128" }}>
                                                    {formatFollowers(applicationData?.followers) || 0}
                                                </Typography>
                                            </>
                                        );
                                    })()}
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    {/* <Rating value={Number(applicationData?.creator_handle_rating ?? 0)} readOnly precision={0.1} /> */}
                                    <Rating value={Number(handleRating)} readOnly precision={0.1} />
                                </Box>
                            </Box>
                        </Box>
                        {niches.length > 0 && (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
                                {visibleNiches.map((niche, index) => (
                                    <ReusableChip key={index} label={niche} size="small" />
                                ))}

                                {hiddenNiches.length > 0 && (
                                    <Tooltip
                                        arrow
                                        placement="bottom"
                                        title={
                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, p: 1 }}>
                                                {hiddenNiches.map((niche, index) => (
                                                    <Typography key={index} variant="body2">{niche}</Typography>
                                                ))}
                                            </Box>
                                        }
                                    >
                                        <Box sx={{ display: "inline-flex" }}>
                                            <ReusableChip
                                                label={`+${hiddenNiches.length}`}
                                                size="small"
                                                sx={{ cursor: "pointer" }}
                                            />
                                        </Box>
                                    </Tooltip>
                                )}
                            </Box>
                        )}
                    </Box>

                    <Box>
                        {applicationData?.closed === true && closeMessage()}
                    </Box>

                    <Box>
                        {applicationData?.Status === "rejected" &&
                            rejectMessage("Pitch", applicationData?.rejection_reasons, applicationData?.other_message)
                        }
                    </Box>

                    <Box>
                        {(() => {
                            const parentDeliverable = applicationData?.deliverables_links?.[0];
                            const childDeliverable = parentDeliverable?.child;
                            const isParentRejected = parentDeliverable?.deliverable_status === "Rejected";
                            const isChildRejected = childDeliverable?.deliverable_status === "Rejected";

                            if (isChildRejected) {
                                return rejectMessage("Deliverable", childDeliverable?.deliverable_reason, "");
                            } else if (isParentRejected) {
                                return rejectMessage("Deliverable", parentDeliverable?.deliverable_reason, "");
                            }
                            return null;
                        })()}
                    </Box>
                    <Box>
                        {applicationData?.live_links?.some(l => l?.child?.link_status === "Rejected") &&
                            rejectMessage("Live Post", "Your live post was rejected due to policy violation.", "")
                        }
                    </Box>

                    <CustomTabs
                        tabs={tabs}
                        activeTab={activeTab}
                        onTabChange={handleTabChange}
                        tabLabels={tabLabels}
                        arrow={true}
                        enabledTabs={enabledTabs}
                        disabledReasons={disabledReasons}
                    />
                    <Box p={1} mb={4}>
                        <TabsGigApplicationData
                            activeTab={activeTab}
                            applicationData={applicationData}
                            setApplicationData={setApplicationData}
                            handleApprovePitch={handleApprovePitch}
                            handleRejectPitch={handleRejectPitch}
                            handleApproveDeliverable={handleApproveDeliverable}
                            handleRejectDeliverable={handleRejectDeliverable}
                            handleRequestChangesClick={handleRequestChangesClick}
                            handleSendFeedback={handleSendFeedback}
                            showFeedbackBox={showFeedbackBox}
                            setShowFeedbackBox={setShowFeedbackBox}
                            feedbackText={feedbackText}
                            setFeedbackText={setFeedbackText}
                            feedbackSentIndexes={feedbackSentIndexes}
                            expandedIndexes={expandedIndexes}
                            toggleExpand={toggleExpand}
                            submittedFeedbackTexts={submittedFeedbackTexts}
                            formatDateTime={formatDateTime}
                            isValidMap={isValidMap}
                            setIsValidMap={setIsValidMap}
                            reasonMap={reasonMap}
                            setReasonMap={setReasonMap}
                            activeTabs={activeTabs}
                            // setActiveTabs={setActiveTabs}
                            setActiveTabs={handleInnerTabChange}
                            handleSave={handleSave}
                            loadingMap={loadingMap}
                            loading={loading}
                            showRejectFeedbackBox={showRejectFeedbackBox}
                            setShowRejectFeedbackBox={setShowRejectFeedbackBox}
                            rejectFeedbackText={rejectFeedbackText}
                            setRejectFeedbackText={setRejectFeedbackText}
                        />
                    </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Box sx={{ backgroundColor: "#fff", p: 3, borderRadius: 2, boxShadow: 1 }}>
                        <Typography sx={{ color: "#7A7A7A", fontWeight: 600, mb: 1, fontSize: '18px' }}>
                            Status Summary
                        </Typography>
                        {["Pitch", "Deliverables", "Live Post"].map((label, i) => {
                            const statusMap = {
                                "Pitch": applicationData?.Status,
                                "Deliverables": applicationData?.deliverables_status,
                                "Live Post": applicationData?.live_status
                            };
                            return (
                                <Box key={i} sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
                                    <Typography sx={{ color: "#7A7A7A", fontSize: '16px' }}>{label}:</Typography>
                                    <ReusableChip label={capitalizeFirstLetter(statusMap[label]) || "Pending"} />
                                </Box>
                            );
                        })}

                        <Divider sx={{ my: 2 }} />
                        <ReusableButton
                            fullWidth
                            variant="contained"
                            classNames={!canBeClosed || applicationData?.closed === true ? "gn-actionbtn-disabled" : "gn-actionbtn"}
                            sx={{
                                textTransform: "none",
                                borderColor: "#ccc",
                                color: "#fff",
                                mt: 2,
                                cursor: !canBeClosed || applicationData?.closed === true ? "not-allowed" : "pointer",
                            }}
                            onClick={handleMarkAsClosed}
                            disabled={!canBeClosed || applicationData?.closed === true || loading.markAsClosed}
                            loader={loading.markAsClosed}
                        >
                            <Box display="flex" alignItems="center" gap={1}>
                                <ReusableMuiIcons name="done" />
                                Mark as Closed
                            </Box>
                        </ReusableButton>

                    </Box>

                    {/* {(applicationData?.closed === false && applicationData?.live_status !== 'Approved' && applicationData?.live_status !== 'Rejected') && (
                        <Box sx={{ backgroundColor: "#fff", p: 3, borderRadius: 2, mt: 2, mb: 2 }}>
                            <Typography sx={{ color: "#7A7A7A", fontWeight: 600, mb: 1, fontSize: '18px' }}>
                                Rate This Influencer
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#999" }}>
                                Rating will be available after live post are approved.
                            </Typography>
                        </Box>
                    )} */}
                </Grid>
            </Grid>
        </Box>
    );
}
